import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import className from 'classnames';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { EntryPoint, LegType, LegTypeProps, TabTypeProp, TabTypePropDefault } from '../../models';
import { TrackingUtils, withPageContext } from '../../utils';

export const AirportSelectorInputDesktop = ({
    activeProduct,
    trackEvent,
    departureOrArrival,
    displayedWord,
    focus,
    hasError,
    onClickInput,
    onChangeInput,
    onRemoveFocus,
    isHotel,
    isDisabled,
}) => {
    const { t } = useTranslation();
    const inputNode = useRef(null);

    useEffect(() => {
        const handleKeyDown = (e) => {
            const key = e.keyCode || e.key;
            if (key === 'Tab' || key === 9) {
                onRemoveFocus();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onRemoveFocus]);

    useEffect(() => {
        if (focus) {
            inputNode.current.focus();
        }
    }, [focus]);

    const getPlaceholderId = () => {
        const typeSearch =
          departureOrArrival === LegType.ARRIVAL && activeProduct === EntryPoint.HOTELS
              ? 'home.hsaSearch'
              : 'citySearch';
        const key = departureOrArrival === LegType.DEPARTURE ? 'departure' : 'destination';
        return `${typeSearch}.${key}`;
    };

    const handleFocus = () => onClickInput();

    const handleChangeInput = (e) => {
        onChangeInput(e.target.value);
    };

    const handleTracking = (label) => {
        const action = TrackingUtils.ACTION[activeProduct.toUpperCase()];
        (activeProduct === EntryPoint.HOTELS || activeProduct === EntryPoint.DP) && trackEvent({ action, label });
    };

    const handleClickInput = (e) => {
        e.target.select();
        const FIELD_NAME_TRACKING = departureOrArrival === LegType.DEPARTURE ? 'dep' : 'dest';
        handleTracking(TrackingUtils.LABEL.fieldOpen(activeProduct, FIELD_NAME_TRACKING));
        onClickInput();
    };

    const inputClassNames = className({
        'odf-z-index-2': hasError,
        'odf-input-inline-first': departureOrArrival === LegType.DEPARTURE || isHotel,
    });
    const placeholder = t(getPlaceholderId());
    return (
        <input
            className={`odf-input-m odf-text-lg odf-input odf-input-inline odf-text-ellipsis ${inputClassNames}`}
            type="text"
            value={displayedWord}
            placeholder={placeholder}
            autoComplete="off"
            spellCheck="false"
            onChange={handleChangeInput}
            onClick={handleClickInput}
            onFocus={handleFocus}
            disabled={isDisabled}
            test-id="input-airport"
            ref={inputNode}
        />
    );
};

AirportSelectorInputDesktop.propTypes = {
    departureOrArrival: LegTypeProps.isRequired,
    displayedWord: PropTypes.string.isRequired,
    onClickInput: PropTypes.func.isRequired,
    onChangeInput: PropTypes.func.isRequired,
    onRemoveFocus: PropTypes.func.isRequired,
    focus: PropTypes.bool,
    hasError: PropTypes.bool,
    isHotel: PropTypes.bool,
    isDisabled: PropTypes.bool,
    activeProduct: TabTypeProp,
    trackEvent: PropTypes.func.isRequired,
};

AirportSelectorInputDesktop.defaultProps = {
    focus: false,
    hasError: false,
    isHotel: false,
    isDisabled: false,
    activeProduct: TabTypePropDefault,
};

const mapStateToProps = ({ activeProduct }) => ({
    activeProduct: activeProduct.activeProduct,
});

const connected = connect(mapStateToProps, {})(withPageContext(AirportSelectorInputDesktop));
export { connected as ConnectedAirportSelectorInputDesktop };
